import type { FC } from 'react';
import type * as React from 'react';
import type { BoxProps } from '@mui/material/Box/Box';
import { forwardRef, useMemo } from 'react';
import type { TextFieldProps, AutocompleteProps, SxProps, Theme } from '@mui/material';
import { Autocomplete, Box, createFilterOptions, TextField, Tooltip, Typography } from '@mui/material';
import type { AutocompleteChangeReason, AutocompleteValue } from '@mui/base/useAutocomplete/useAutocomplete';
import type { Option, OptionValue } from 'types/shared';

interface RuleCommonAutocompleteProps
  extends Pick<AutocompleteProps<OptionValue, false, boolean, false>, 'disableClearable'> {
  value?: OptionValue;
  onChange: (value: OptionValue) => void;
  InputProps?: TextFieldProps;
  id: string;
  isLoading?: boolean;
  options: Option[];
  allSelectable?: boolean;
  sx?: SxProps<Theme>;
}

interface TooltipRefProps extends BoxProps {
  tooltip?: string;
}

const TooltipRef = forwardRef(({ tooltip, ...props }: TooltipRefProps) => {
  return (
    <Tooltip title={tooltip} placement={'right'} arrow>
      <Box component="li" {...props} sx={{ p: 0, m: 0 }}>
        {props.children}
      </Box>
    </Tooltip>
  );
});
TooltipRef.displayName = 'TooltipRef';

const RuleCommonAutocomplete: FC<RuleCommonAutocompleteProps> = ({
  id,
  options,
  isLoading,
  onChange,
  value,
  InputProps,
  ...props
}) => {
  const filter = createFilterOptions<Option>();

  const derivedValue = useMemo(() => options.find((o) => value === o.value), [value, options]);

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: AutocompleteValue<Option, false, false, false>,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'selectOption' || reason === 'removeOption') {
      onChange(newValue?.value);
    } else if (reason === 'clear') {
      onChange('');
    }
  };

  return (
    <Autocomplete
      {...props}
      size="small"
      multiple={false}
      disabled={isLoading ?? InputProps?.disabled}
      loading={isLoading}
      id={id}
      options={options}
      disableClearable
      renderOption={(props, option, state, ownerState) => {
        const { ...optionProps } = props;
        return (
          <TooltipRef tooltip={option?.tooltip}>
            <Box
              component="li"
              {...optionProps}
              sx={{
                opacity: option?.disabled ? 0.55 : 1,
                pointerEvents: option?.disabled ? 'none' : 'auto',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: option?.icon ? 'space-between' : 'start',
                }}
              >
                <Typography variant="body2">{ownerState.getOptionLabel(option)}</Typography>
                {option?.icon}
              </Box>
            </Box>
          </TooltipRef>
        );
      }}
      value={derivedValue ?? { label: '', value: '' }} // eslint-disable-line
      isOptionEqualToValue={(option: Option, value?: Option) => {
        if (!value || value.value === '') return false; // ignore the default value
        return option.value === value.value;
      }}
      getOptionLabel={(option) => option.label}
      onChange={handleChange}
      style={{ width: '100%' }}
      renderInput={(params) => (
        <TextField {...params} label={InputProps?.label} placeholder={InputProps?.placeholder} />
      )}
      filterOptions={filter}
    />
  );
};

export default RuleCommonAutocomplete;
